import React from 'react';
import { TokenEstimate, formatTokenCount, TOKEN_BUDGET } from '../../utils/tokenEstimator';
import { isCommercial } from '../../config/appMode';

// Full number với thousand separator: 54234 → "54,234"; 1234 → "1,234"; 500 → "500"
const formatCompact = (n: number): string => n.toLocaleString('en-US');

interface TokenBadgeProps {
    /** @deprecated US-04-10: no longer used cho display — chỉ giữ signature cho callers chưa migrate */
    estimate?: TokenEstimate;
    showBar?: boolean;
    compact?: boolean;
    /**
     * Primary credit number to display (resolved by `selectPrimaryCredits` per mode).
     * `null` → render "Chưa đếm" pill (file pre-preflight).
     */
    credits?: number | null;
    /** Explicit status — 4 distinct visuals: idle / counting / ok / error */
    accurateStatus?: 'idle' | 'counting' | 'ok' | 'error';
    onRetryAccurate?: () => void;
}

const STATUS_CONFIG = {
    safe: {
        bg: '#ecfdf5',
        border: '#a7f3d0',
        text: '#065f46',
        bar: '#10b981',
        label: 'An toan',
    },
    warning: {
        bg: '#fffbeb',
        border: '#fde68a',
        text: '#92400e',
        bar: '#f59e0b',
        label: 'Can than',
    },
    danger: {
        bg: '#fef2f2',
        border: '#fecaca',
        text: '#991b1b',
        bar: '#ef4444',
        label: 'Vuot gioi han!',
    },
};

const CheckIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
);

const WarningIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
);

const DangerIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="15" y1="9" x2="9" y2="15" />
        <line x1="9" y1="9" x2="15" y2="15" />
    </svg>
);

const StatusIcon: React.FC<{ status: TokenEstimate['status'] }> = ({ status }) => {
    switch (status) {
        case 'safe': return <CheckIcon />;
        case 'warning': return <WarningIcon />;
        case 'danger': return <DangerIcon />;
    }
};

/**
 * Derive token-budget status from credit number.
 * Treats credits as token-equivalent for budget gating (1 credit ≈ 1 token).
 */
const statusFromCredits = (credits: number | null | undefined): TokenEstimate['status'] => {
    const t = credits ?? 0;
    if (t >= TOKEN_BUDGET) return 'danger';
    if (t >= TOKEN_BUDGET * 0.75) return 'warning';
    return 'safe';
};

/**
 * TokenBadge — color-coded credit/token pill for file rows + summary cards.
 *
 * Refactored 2026-04-28: drops `accurateCount: AccurateTokenCount` shape, takes
 * pre-resolved `credits: number | null` from `selectPrimaryCredits` (mode-aware).
 * Status enum stays explicit (idle / counting / ok / error) — 4 distinct visuals.
 */
const TokenBadge: React.FC<TokenBadgeProps> = ({
    estimate,
    showBar = true,
    compact = false,
    credits,
    accurateStatus = 'idle',
    onRetryAccurate,
}) => {
    const derivedStatus: TokenEstimate['status'] =
        estimate?.status ?? statusFromCredits(credits);
    const config = STATUS_CONFIG[derivedStatus];
    // Legacy `estimate` prop counts as ready for back-compat callers; new callers use credits + accurateStatus
    const isReady = !!estimate || (accurateStatus === 'ok' && typeof credits === 'number');

    if (compact) {
        // Counting state — spinner pill
        if (accurateStatus === 'counting') {
            return (
                <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                    padding: '2px 8px', borderRadius: '9999px', fontSize: '12px',
                    color: '#64748b', backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0',
                }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ animation: 'spin 1s linear infinite' }}>
                        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                    <span style={{ fontSize: 11 }}>tính toán…</span>
                </span>
            );
        }
        // Error state — red pill + retry
        if (accurateStatus === 'error') {
            return (
                <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                    padding: '2px 8px', borderRadius: '9999px', fontSize: '12px',
                    color: '#991b1b', backgroundColor: '#fef2f2', border: '1px solid #fecaca',
                }}>
                    <DangerIcon />
                    <span style={{ fontSize: 11 }}>lỗi đếm</span>
                    {onRetryAccurate && (
                        <button
                            onClick={(e: React.MouseEvent) => { e.stopPropagation(); onRetryAccurate(); }}
                            style={{ marginLeft: 4, color: '#991b1b', textDecoration: 'underline', fontSize: 11, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                            title="Thử lại đếm chính xác"
                        >Thử lại</button>
                    )}
                </span>
            );
        }
        // Ready state — show credit number (require numeric credits, ignore legacy estimate-only callers in compact mode)
        if (isReady && typeof credits === 'number') {
            const tooltipText = `${formatCompact(credits)} ${isCommercial ? 'credits' : 'tokens'}`;
            return (
                <div
                    style={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        padding: '2px 8px', borderRadius: '9999px', fontSize: '12px', fontWeight: 600,
                        color: config.text, backgroundColor: config.bg, border: `1px solid ${config.border}`,
                        whiteSpace: 'nowrap',
                    }}
                    title={tooltipText}
                >
                    <StatusIcon status={derivedStatus} />
                    <span>{formatCompact(credits!)}</span>
                    {isCommercial && <span style={{ opacity: 0.6, fontSize: 10, fontWeight: 400 }}>credits</span>}
                </div>
            );
        }
        // Idle state — chưa fire bundle yet, hiện như counting để consistent UX
        return (
            <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                padding: '2px 8px', borderRadius: '9999px', fontSize: '12px',
                color: '#64748b', backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0',
            }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ animation: 'spin 1s linear infinite' }}>
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                <span style={{ fontSize: 11 }}>tính toán…</span>
            </span>
        );
    }

    // Non-compact (full badge + bar) — same 4 status states as compact, just larger layout
    if (accurateStatus === 'counting') {
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: '140px', padding: '4px 0', fontSize: '12px', color: '#64748b' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ animation: 'spin 1s linear infinite' }}>
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                <span>tính toán…</span>
            </div>
        );
    }
    if (accurateStatus === 'error') {
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: '140px', padding: '4px 0', fontSize: '12px', color: '#991b1b' }}>
                <DangerIcon />
                <span>lỗi đếm</span>
                {onRetryAccurate && (
                    <button
                        onClick={(e: React.MouseEvent) => { e.stopPropagation(); onRetryAccurate(); }}
                        style={{ marginLeft: 4, color: '#991b1b', textDecoration: 'underline', fontSize: 12, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        title="Thử lại đếm chính xác"
                    >Thử lại</button>
                )}
            </div>
        );
    }
    if (!isReady) {
        // Idle — chưa fire bundle yet, hiện như counting để consistent UX
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: '140px', padding: '4px 0', fontSize: '12px', color: '#64748b' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ animation: 'spin 1s linear infinite' }}>
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                <span>tính toán…</span>
            </div>
        );
    }

    const percent = estimate?.percentage
        ?? (typeof credits === 'number' ? Math.min(100, (credits / TOKEN_BUDGET) * 100) : 0);

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                minWidth: '140px',
            }}
        >
            <div
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '3px 10px',
                    borderRadius: '9999px',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: config.text,
                    backgroundColor: config.bg,
                    border: `1px solid ${config.border}`,
                    whiteSpace: 'nowrap',
                    width: 'fit-content',
                }}
            >
                <StatusIcon status={derivedStatus} />
                <span>{estimate?.statusLabel ?? config.label}</span>
                <span style={{ opacity: 0.6, fontWeight: 400 }}>
                    {config.label}
                </span>
            </div>

            {showBar && (
                <div
                    style={{
                        height: '4px',
                        backgroundColor: '#e2e8f0',
                        borderRadius: '2px',
                        overflow: 'hidden',
                        width: '100%',
                    }}
                >
                    <div
                        style={{
                            height: '100%',
                            width: `${percent}%`,
                            backgroundColor: config.bar,
                            borderRadius: '2px',
                            transition: 'width 0.3s ease',
                        }}
                    />
                </div>
            )}
        </div>
    );
};

/**
 * TokenSummaryCard — overview card for batch uploads showing aggregate stats.
 * Refactored 2026-04-28: takes `creditsList: (number|null)[]` + `statusList`
 * (parallel arrays per file). Sums non-null entries only.
 */
export const TokenSummaryCard: React.FC<{
    creditsList?: (number | null | undefined)[];
    statusList?: ('idle' | 'counting' | 'ok' | 'error')[];
    totalFiles: number;
}> = ({ creditsList = [], statusList = [], totalFiles }) => {
    if (totalFiles === 0) return null;

    const okCount = statusList.filter(s => s === 'ok').length;
    const errorCount = statusList.filter(s => s === 'error').length;
    const countingCount = statusList.filter(s => s === 'counting').length;
    const allDone = okCount === totalFiles;

    // Tổng credits chỉ tính từ entries non-null + status === 'ok'
    const totalCredits = creditsList.reduce<number>((sum, c, i) => {
        if (statusList[i] !== 'ok' || typeof c !== 'number') return sum;
        return sum + c;
    }, 0);

    const overallStatus: TokenEstimate['status'] =
        totalCredits >= TOKEN_BUDGET ? 'danger' :
        (countingCount > 0 || errorCount > 0) ? 'warning' :
        'safe';
    const config = STATUS_CONFIG[overallStatus];

    return (
        <div
            style={{
                padding: '12px 16px',
                borderRadius: '12px',
                backgroundColor: config.bg,
                border: `1px solid ${config.border}`,
                marginBottom: '16px',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <StatusIcon status={overallStatus} />
                    <span style={{ fontSize: '14px', fontWeight: 700, color: config.text }}>
                        Token Summary
                    </span>
                </div>
                <span style={{ fontSize: '13px', fontWeight: 600, color: config.text }}>
                    {allDone ? (
                        <>Tổng: {formatCompact(totalCredits)} {isCommercial ? 'credits' : 'tokens'}</>
                    ) : countingCount > 0 ? (
                        <span style={{ color: '#64748b' }}>Đang tính toán chính xác...</span>
                    ) : okCount > 0 ? (
                        <>Đã tính {okCount}/{totalFiles}: {formatCompact(totalCredits)} {isCommercial ? 'credits' : 'tokens'}</>
                    ) : (
                        <span style={{ color: '#64748b' }}>Chưa tính được</span>
                    )}
                </span>
            </div>

            <div style={{ display: 'flex', gap: '12px', fontSize: '12px' }}>
                <span style={{ color: STATUS_CONFIG.safe.text, fontWeight: 500 }}>
                    {okCount}/{totalFiles} đã tính
                </span>
                {countingCount > 0 && (
                    <span style={{ color: '#64748b', fontWeight: 500 }}>
                        {countingCount} đang tính…
                    </span>
                )}
                {errorCount > 0 && (
                    <span style={{ color: STATUS_CONFIG.danger.text, fontWeight: 500 }}>
                        {errorCount} lỗi tính
                    </span>
                )}
                <span style={{ color: '#64748b', marginLeft: 'auto' }}>
                    Budget: {formatTokenCount(TOKEN_BUDGET)} tokens/file
                </span>
            </div>
        </div>
    );
};

export default TokenBadge;
